import { useMemo } from 'react';
import styled from 'styled-components';
import {
  FormField,
  Checkbox,
  FlexColumn,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import { FormControlLabel as FormControlLabelBase } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

const supportedAnomalyDetectionMonitorsCategories: string[] = [
  'automated_volume_anomalies',
  'automated_freshness_anomalies',
  'metric_anomalies',
  'pipeline_performance',
];

const supportedDataTestsCategories: string[] = [
  'custom_sql_tests',
  'dbt_generic_tests',
  'dbt_source_freshness',
  'python_sdk',
];

const FormControlLabel = styled(FormControlLabelBase)`
  gap: 8px;

  .MuiFormControlLabel-label {
    font-size: 14px;
  }
`;

type Props = {
  fieldName: string;
  title: string;
  isDisabled?: boolean;
};

const RuleCategoriesFormContent = ({ fieldName, title, isDisabled }: Props) => {
  const { data: account } = useAccount();
  const methods = useFormContext();

  const cloudTestsEnabled = useMemo(
    () => account?.cloud_tests_enabled,
    [account],
  );

  const isAllAnomalyDetectionMonitorsAlertCategoriesSelected =
    supportedAnomalyDetectionMonitorsCategories.every((item) =>
      methods.watch(fieldName)?.includes(item),
    );

  const isSomeAnomalyDetectionMonitorsCategoriesSelected =
    supportedAnomalyDetectionMonitorsCategories.some((item) =>
      methods.watch(fieldName)?.includes(item),
    );

  const isAllDataTestsCategoriesSelected = supportedDataTestsCategories.every(
    (item) => methods.watch(fieldName)?.includes(item),
  );

  const isSomeDataTestsCategoriesSelected = supportedDataTestsCategories.some(
    (item) => methods.watch(fieldName)?.includes(item),
  );

  const isCategorySelected = (category: string): boolean =>
    !!methods.watch(fieldName)?.includes(category);

  const handleCategoriesChange = (values: string[], checked: boolean) => {
    const existingValues = methods.watch(fieldName) || [];

    if (checked) {
      const newCategories = Array.from(new Set([...existingValues, ...values]));
      methods.setValue(fieldName, newCategories, { shouldValidate: true });
    } else {
      const newCategories = existingValues.filter(
        (item: string) => !values.includes(item),
      );
      methods.setValue(fieldName, newCategories, { shouldValidate: true });
    }
  };

  return (
    <FormField
      title={title}
      errorMessage={methods.formState.errors[fieldName]?.message as string}
    >
      <Controller
        control={methods.control}
        name={fieldName}
        rules={{ required: requiredLabel }}
        render={({ field }) => (
          <>
            {cloudTestsEnabled && (
              <>
                <FormControlLabel
                  label="Anomaly detection monitors"
                  sx={{ ml: 0 }}
                  control={
                    <Checkbox
                      {...field}
                      disabled={isDisabled}
                      checked={
                        isAllAnomalyDetectionMonitorsAlertCategoriesSelected
                      }
                      indeterminate={
                        isSomeAnomalyDetectionMonitorsCategoriesSelected &&
                        !isAllAnomalyDetectionMonitorsAlertCategoriesSelected
                      }
                      onChange={(e, val) =>
                        handleCategoriesChange(
                          supportedAnomalyDetectionMonitorsCategories,
                          val,
                        )
                      }
                    />
                  }
                />
                <FlexColumn gap={1} sx={{ ml: 5 }}>
                  <FormControlLabel
                    label="Automated volume anomalies"
                    sx={{ ml: 0 }}
                    control={
                      <Checkbox
                        {...field}
                        disabled={isDisabled}
                        checked={isCategorySelected(
                          'automated_volume_anomalies',
                        )}
                        onChange={(e, val) =>
                          handleCategoriesChange(
                            ['automated_volume_anomalies'],
                            val,
                          )
                        }
                      />
                    }
                  />
                  <FormControlLabel
                    label="Automated freshness anomalies"
                    sx={{ ml: 0 }}
                    control={
                      <Checkbox
                        {...field}
                        disabled={isDisabled}
                        checked={isCategorySelected(
                          'automated_freshness_anomalies',
                        )}
                        onChange={(e, val) =>
                          handleCategoriesChange(
                            ['automated_freshness_anomalies'],
                            val,
                          )
                        }
                      />
                    }
                  />
                  <FormControlLabel
                    label="Metric anomalies"
                    sx={{ ml: 0 }}
                    control={
                      <Checkbox
                        {...field}
                        disabled={isDisabled}
                        checked={isCategorySelected('metric_anomalies')}
                        onChange={(e, val) =>
                          handleCategoriesChange(['metric_anomalies'], val)
                        }
                      />
                    }
                  />
                  <FormControlLabel
                    label="Pipeline task performance"
                    sx={{ ml: 0 }}
                    control={
                      <Checkbox
                        {...field}
                        disabled={isDisabled}
                        checked={isCategorySelected('pipeline_performance')}
                        onChange={(e, val) =>
                          handleCategoriesChange(['pipeline_performance'], val)
                        }
                      />
                    }
                  />
                </FlexColumn>
              </>
            )}
            <FormControlLabel
              label="Data tests"
              sx={{ ml: 0 }}
              control={
                <Checkbox
                  {...field}
                  disabled={isDisabled}
                  checked={isAllDataTestsCategoriesSelected}
                  indeterminate={
                    isSomeDataTestsCategoriesSelected &&
                    !isAllDataTestsCategoriesSelected
                  }
                  onChange={(e, val) =>
                    handleCategoriesChange(supportedDataTestsCategories, val)
                  }
                />
              }
            />
            <FlexColumn gap={1} sx={{ ml: 5 }}>
              <FormControlLabel
                label="dbt tests (generic tests including dbt packages)"
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    {...field}
                    disabled={isDisabled}
                    checked={isCategorySelected('dbt_generic_tests')}
                    onChange={(e, val) =>
                      handleCategoriesChange(['dbt_generic_tests'], val)
                    }
                  />
                }
              />
              <FormControlLabel
                label="Custom SQL tests"
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    {...field}
                    disabled={isDisabled}
                    checked={isCategorySelected('custom_sql_tests')}
                    onChange={(e, val) =>
                      handleCategoriesChange(['custom_sql_tests'], val)
                    }
                  />
                }
              />
              <FormControlLabel
                label="dbt source freshness"
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    {...field}
                    disabled={isDisabled}
                    checked={isCategorySelected('dbt_source_freshness')}
                    onChange={(e, val) =>
                      handleCategoriesChange(['dbt_source_freshness'], val)
                    }
                  />
                }
              />
              <FormControlLabel
                label="Python SDK tests"
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    {...field}
                    disabled={isDisabled}
                    checked={isCategorySelected('python_sdk')}
                    onChange={(e, val) =>
                      handleCategoriesChange(['python_sdk'], val)
                    }
                  />
                }
              />
            </FlexColumn>

            <FormControlLabel
              label="Model errors"
              sx={{ ml: 0 }}
              control={
                <Checkbox
                  {...field}
                  disabled={isDisabled}
                  checked={isCategorySelected('model_build')}
                  onChange={(e, val) =>
                    handleCategoriesChange(['model_build'], val)
                  }
                />
              }
            />

            <FormControlLabel
              label="Job failures"
              sx={{ ml: 0 }}
              control={
                <Checkbox
                  {...field}
                  disabled={isDisabled}
                  checked={isCategorySelected('pipeline')}
                  onChange={(e, val) =>
                    handleCategoriesChange(['pipeline'], val)
                  }
                />
              }
            />
          </>
        )}
      />
    </FormField>
  );
};

export default RuleCategoriesFormContent;
